import React, { useEffect, useState } from 'react';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { CustomTooltip } from 'Views/Components/_HumanWritten/CustomTooltip/CustomTooltip';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import {
	ClearFiltersButton,
	countActiveFilters,
} from 'Views/Components/_HumanWritten/StaffTables/Shared/ClearFiltersButton';
import { StaffFiltersDatePicker } from 'Views/Components/_HumanWritten/StaffTables/StaffFiltersDatePicker';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import If from 'Views/Components/If/If';
import { getParkingLocations, ParkingLocationDto } from 'Services/Api/_HumanWritten/ParkingService';
import {
	exportParkingReservationsWithFilters,
	exportSelectedParkingReservations,
} from 'Services/Api/_HumanWritten/ReportService';
import {
	defaultFilters as defaultParkingReservationFilters,
	useStaffParkingReservationsStore,
} from './StaffParkingReservationsFilterStore';
import { StaffParkingReservationsColumnSelector } from './StaffParkingReservationsColumnSelector';

export interface StaffParkingReservationFiltersProps {
	setRefresh: () => void;
	totalReservationCount: number;
	hideCustomerColumn?: boolean;
}

const statusOptions = [
	{ name: 'Active', value: 'ACTIVE', tagClassName: 'tag--success' },
	{ name: 'Upcoming', value: 'CONFIRMED', tagClassName: 'tag--success' },
	{ name: 'Passed', value: 'COMPLETED', tagClassName: 'grey' },
	{ name: 'Pending', value: 'PENDING', tagClassName: 'tag--warning' },
	{ name: 'Cancelled', value: 'CANCELLED', tagClassName: 'tag--error' },
	{ name: 'Expired', value: 'EXPIRED', tagClassName: 'grey' },
];

const MAX_EXPORT_COUNT = 4000;

const StaffParkingReservationFilters = ({
	setRefresh,
	totalReservationCount,
	hideCustomerColumn,
}: StaffParkingReservationFiltersProps) => {
	const {
		filters,
		selectedStatuses,
		selectedLocations,
		selectedReservationIds,
		visibleColumns,
		isExporting,
		setSelectedStatuses,
		setSelectedLocations,
		setSearchString,
		updateFilters,
		resetFilters,
		setIsExporting,
	} = useStaffParkingReservationsStore();

	const [locations, setLocations] = useState<ParkingLocationDto[]>([]);
	const [tempSelectedStatuses, setTempSelectedStatuses] = useState<string[]>([]);
	const [tempSelectedLocations, setTempSelectedLocations] = useState<string[]>([]);
	const [tempSearchString, setTempSearchString] = useState(
		() => new URLSearchParams(window.location.search).get('search') || filters.searchString || '',
	);

	// Fetch parking locations on mount
	useEffect(() => {
		getParkingLocations().then(setLocations);
	}, []);

	// Sync temp state when store state changes
	useEffect(() => {
		setTempSelectedStatuses(selectedStatuses);
	}, [selectedStatuses]);

	useEffect(() => {
		setTempSelectedLocations(selectedLocations);
	}, [selectedLocations]);

	// On mount: if URL param exists, apply to store and clean URL
	useEffect(() => {
		const urlSearch = new URLSearchParams(window.location.search).get('search');
		if (urlSearch) {
			setSearchString(urlSearch);
			setRefresh();
			const url = new URL(window.location.href);
			url.searchParams.delete('search');
			window.history.replaceState({}, '', url.pathname + url.search);
		}
	}, []);

	// Debounce: user typing -> store
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (tempSearchString !== (filters.searchString || '')) {
				setSearchString(tempSearchString);
				setRefresh();
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [tempSearchString]);

	const arraysEqual = (arr1: any[], arr2: any[]) => {
		if (arr1.length !== arr2.length) return false;
		const sorted1 = [...arr1].sort();
		const sorted2 = [...arr2].sort();
		return sorted1.every((value, index) => value === sorted2[index]);
	};

	const onStatusAfterClose = () => {
		const statusesChanged = !arraysEqual(tempSelectedStatuses, selectedStatuses);

		if (tempSelectedStatuses.length === 0) {
			setTempSelectedStatuses(selectedStatuses);
			return;
		}

		if (statusesChanged) {
			setSelectedStatuses(tempSelectedStatuses);
			requestAnimationFrame(() => {
				setRefresh();
			});
		}
	};

	const onLocationAfterClose = () => {
		const locationsChanged = !arraysEqual(tempSelectedLocations, selectedLocations);

		if (locationsChanged) {
			setSelectedLocations(tempSelectedLocations);
			requestAnimationFrame(() => {
				setRefresh();
			});
		}
	};

	const getStatusDisplay = () => {
		if (selectedStatuses.length === 0 || selectedStatuses.length === statusOptions.length) {
			return 'All';
		}
		return selectedStatuses
			.map(s => statusOptions.find(opt => opt.value === s))
			.filter(Boolean)
			.map(opt => <Tag key={opt!.value} text={opt!.name} tagClassName={opt!.tagClassName} />);
	};

	const handleDatePickerChange = (startDate: Date | undefined, endDate: Date | undefined) => {
		updateFilters({
			startDate,
			endDate,
			pageNo: 0,
		});
		setRefresh();
	};

	const getLocationDisplay = () => {
		if (selectedLocations.length === 0 || selectedLocations.length === locations.length) {
			return 'All';
		}
		return selectedLocations
			.map(id => locations.find(l => l.id === id))
			.filter(Boolean)
			.map(loc => <Tag key={loc!.id} text={loc!.name} tagClassName="grey" />);
	};

	const isAllStatusesSelected = tempSelectedStatuses.length === statusOptions.length;
	const isAllLocationsSelected =
		tempSelectedLocations.length === 0 ||
		(locations.length > 0 && tempSelectedLocations.length === locations.length);

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const columns = visibleColumns.filter(col => col.visible).map(col => col.key);

			if (selectedReservationIds.size > 0) {
				await exportSelectedParkingReservations(Array.from(selectedReservationIds), columns);
			} else {
				await exportParkingReservationsWithFilters({
					searchString: filters.searchString || undefined,
					startDate: filters.startDate,
					endDate: filters.endDate,
					dateFilterType: filters.dateFilterType,
					statusFilters: filters.statusFilters,
					locationFilters: filters.locationFilters,
					columns,
				});
			}
		} finally {
			setIsExporting(false);
		}
	};

	const exportCount =
		selectedReservationIds.size > 0
			? selectedReservationIds.size
			: Math.min(totalReservationCount, MAX_EXPORT_COUNT);

	return (
		<div className="table-filters__container">
			<div className="left">
				{/* Date Range */}
				<StaffFiltersDatePicker
					options={['Created Date', 'Parking Date']}
					startDate={filters.startDate}
					endDate={filters.endDate}
					filterByCreatedDate={filters.dateFilterType === 'created'}
					allowFutureDates
					onDateChange={handleDatePickerChange}
					onFilterTypeChange={isCreatedDate => {
						updateFilters({
							dateFilterType: isCreatedDate ? 'created' : 'parking',
							pageNo: 0,
						});
						setRefresh();
					}}
				/>

				{/* Status Filter */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onStatusAfterClose}
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Status</p>
								<span className="tags">{getStatusDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllStatusesSelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedStatuses([]);
									} else {
										setTempSelectedStatuses(statusOptions.map(opt => opt.value));
									}
								}}
							/>
							{statusOptions.map(statusOption => (
								<Checkbox
									className="fh-popover__checkbox fh-popover__list-item"
									key={statusOption.value}
									model={{ value: tempSelectedStatuses.includes(statusOption.value) }}
									modelProperty="value"
									label={statusOption.name}
									onChecked={(_, value) => {
										if (value) {
											setTempSelectedStatuses([...tempSelectedStatuses, statusOption.value]);
										} else {
											setTempSelectedStatuses(
												tempSelectedStatuses.filter(x => x !== statusOption.value),
											);
										}
									}}
								/>
							))}
						</div>
					)}
				/>

				{/* Location Filter */}
				<If condition={locations.length > 0}>
					<Popover
						popoverClassName="fh-popover"
						onAfterClose={onLocationAfterClose}
						buttonContent={(() => {
							return (
								<>
									<p className="grey">Location</p>
									<span className="tags">{getLocationDisplay()}</span>
								</>
							);
						})()}
						popoverContent={() => (
							<div>
								<Checkbox
									className="fh-popover__checkbox fh-popover__list-item"
									model={{ value: isAllLocationsSelected }}
									modelProperty="value"
									label="All"
									onChecked={(_, value) => {
										if (!value) {
											setTempSelectedLocations(locations.map(l => l.id));
										} else {
											setTempSelectedLocations([]);
										}
									}}
								/>
								{locations.map(location => (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={location.id}
										model={{
											value: tempSelectedLocations.includes(location.id),
										}}
										modelProperty="value"
										label={location.name}
										onChecked={(_, value) => {
											if (value) {
												setTempSelectedLocations([...tempSelectedLocations, location.id]);
											} else {
												setTempSelectedLocations(
													tempSelectedLocations.filter(x => x !== location.id),
												);
											}
										}}
									/>
								))}
							</div>
						)}
					/>
				</If>

				{/* Search Input */}
				<form onSubmit={e => e.preventDefault()}>
					<div className="search-box">
						<input
							id="parking-reservation-search-input"
							placeholder="Search"
							className="fh-basic-search"
							autoComplete="off"
							value={tempSearchString}
							onChange={e => setTempSearchString(e.target.value)}
						/>
						<If condition={stringIsEmpty(tempSearchString)}>
							<CustomTooltip
								id="parking-reservation-search-tool-tip"
								content="Search by Reservation ID, Customer Name, Email, Rego, Transaction ID"
								toolTipPosition="bottom-right"
							/>
						</If>
						<If condition={stringNotEmpty(tempSearchString)}>
							<Button
								className="clear-search"
								onClick={e => {
									e.preventDefault();
									setTempSearchString('');
								}}
								colors={Colors.Black}
								display={Display.Text}
							>
								<Icon name="cross" />
							</Button>
						</If>
					</div>
				</form>
				<ClearFiltersButton
					activeCount={countActiveFilters(filters, defaultParkingReservationFilters)}
					onClear={() => {
						resetFilters();
						setTempSearchString('');
						setRefresh();
					}}
					dataTestId="parking-reservations-clear-filters"
				/>
			</div>
			<div className="right">
				<StaffParkingReservationsColumnSelector hideCustomerColumn={hideCustomerColumn} />
				<Button
					display={Display.Solid}
					colors={Colors.Secondary}
					className="export-button"
					newStyles
					disabled={isExporting || (selectedReservationIds.size === 0 && totalReservationCount === 0)}
					onClick={handleExport}
				>
					<Icon name="download" useMaterialIcons />
					{isExporting ? 'Exporting...' : `Export (${exportCount})`}
				</Button>
			</div>
		</div>
	);
};

export default StaffParkingReservationFilters;
